<template>
  <div class="content">
    <sub-header v-if="theme" />
    <component :is="pageTemplate" v-if="pageTemplate && $isNotNullOrUndefined(content)" :content="content" />
  </div>
</template>

<script>
import { vwoCode } from '@/utils/vwo.js'

// Import all page templates
const components = {
  frontPage: () => import('@/components/templates/FrontPage'),
  contentPage: () => import('@/components/templates/ContentPage'),
  event: () => import('@/components/templates/ContentPage'),
  project: () => import('@/components/templates/ContentPage'),
  blog: () => import('@/components/templates/ContentPage'),
  receiptPage: () => import('@/components/templates/ContentPage'),
  articlesPage: () => import('@/components/templates/ContentPage'),
  basketPage: () => import('@/components/templates/BasketPage'),
  searchPage: () => import('@/components/templates/SearchPage'),
  customerCenterPage: () => import('@/components/templates/CustomerCenterPage'),
  requestNewPasswordPage: () => import('@/components/templates/requestNewPasswordPage'),
  editUserPage: () => import('@/components/templates/EditUserPage'),
  SubHeader: () => import('@/components/organisms/SubHeader'),
  productPage: () => import('@/components/templates/ContentPage'),
  brandPage: () => import('@/components/templates/ContentPage'),
  categories: () => import('@/components/templates/ContentPage'),
  checkoutPage: () => import('@/components/templates/ContentPage'),
  impersonationPage: () => import('@/components/templates/ContentPage'),
  createUserPage: () => import('@/components/templates/ContentPage'),
  favoritePage: () => import('@/components/templates/ContentPage'),
  assortmentsPage: () => import('@/components/templates/ContentPage'),
  latestBoughtProductsPage: () => import('@/components/templates/ContentPage'),
  myAccountPage: () => import('@/components/templates/ContentPage'),
  resetPasswordPage: () => import('@/components/templates/ContentPage'),
  recipe: () => import('@/components/templates/ContentPage')
}

export default {
  components,
  watchQuery: ['q'],
  // Key for <NuxtChild> (transitions)
  key: (to) => to.fullPath,
  // Called to know which transition to apply
  transition(to, from) {
    if (!from) {
      return 'slide-up'
    }
    return +to.query.page < +from.query.page ? 'slide-down' : 'slide-up'
  },
  async asyncData({ payload }) {
    return payload
  },
  async fetch() {
    if (this.meta.type !== 'ContentProduct') {
      return
    }
    this.product = await this.$repositories.product.byGuid(this.currentCulture, this.productGuid)
    let productAssets = await this.$repositories.assets.bySkus([this.product?.sku])
    let productAsset = productAssets ? productAssets[0] : null

    // Add images to product object
    let packshots = productAsset?.data?.pack
      ? productAsset?.data?.pack?.map((shot) => {
          return {
            name: this.product.title,
            url: `${process.env.umbracoBaseUrl}/kontainer/${process.env.kontainerUrl}/${shot.url}${this.getImageCrops()}`
          }
        })
      : []
    let images = productAsset?.data?.images
      ? productAsset?.data?.images?.map((image) => {
          return {
            name: this.product.title,
            url: `${process.env.umbracoBaseUrl}/kontainer/${process.env.kontainerUrl}/${image.url}${this.getImageCrops()}`
          }
        })
      : []
    this.product.images = [...packshots, ...images]
  },
  data() {
    return {
      product: null
    }
  },
  computed: {
    pageTemplate() {
      if (this.meta?.template) {
        return this.meta.template
      } else {
        console.warn(`The template for '${this.meta?.template}' was not found.`)
        return 'contentPage' // This is just a default for now
      }
    },
    orgSchema() {
      return this.$store.getters['site/getSiteSettings']?.organizationSchema
    },
    logo() {
      return process.env.umbracoBaseUrl + this.$store.getters['site/getSiteSettings']?.seoImage?.url
    },
    currentCulture() {
      return this.$store.getters['site/getCurrentCulture']
    },
    productGuid() {
      return this.$store.getters['content/content/getContent']?.meta?.id
    },
    pageUid() {
      return this.$store.getters['content/content/getPageUid']
    },
    theme() {
      return this.$store.getters['content/content/getTheme']
    },
    hostname() {
      if (this.meta?.hostName?.includes('https')) {
        return this.meta?.hostName
      } else {
        return 'https://' + this.meta?.hostName
      }
    }
  },
  created() {
    const authQueryToken = this.$route.query?.token
    if (process.client && authQueryToken) {
      window.history.replaceState(null, null, this.$route.path)
    }
  },
  layout: ({ route, payload, layout }) => {
    if (payload?.meta?.template == 'customerCenterPage' && route.query?.orderId) {
      return 'Blank'
    } else {
      return layout
    }
  },
  methods: {
    getImageCrops() {
      return '?mode=crop&width=1200&height=630'
    },
    getCenter(focalPoint) {
      return Object.values(focalPoint).reverse().join(',')
    },
    getImageUrl(image) {
      return `${this.cdnUrl}${image[0].url}${this.getImageCrops()}`
    },
    isEcomPageType(type) {
      return type === 'ContentProductList' || type === 'ContentProduct' || type === 'ContentCategory'
    },
    metaTitle(type) {
      if (this.isEcomPageType(type)) {
        return this.meta?.title
      } else {
        return this.seo?.metaTitle ?? this.meta?.title
      }
    },
    metaDesc(type) {
      if (this.isEcomPageType(type)) {
        return this.meta?.metaDescription
      } else {
        return this.seo?.metaDescription ?? this.meta?.metaDescription
      }
    },
    firstDefined(...text) {
      for (let i = 0; i < text.length; i++) {
        if (text[i]) {
          return text[i]
        }
      }
      return ''
    }
  },
  head() {
    // Set all SEO stuff inside the head
    if (!this.seo) {
      return {}
    } else {
      if (this.seo.hrefLang) {
        this.seo.hrefLang.forEach((hrefLang) => {
          let hreflangObject = {}
          hreflangObject.rel = 'alternate'
          hreflangObject.href = hrefLang.url
          hreflangObject.hreflang = hrefLang.culture
        })
      }
      const robots = ['test.bronnum.dk', 'test-api.bronnum.dk', 'test-backoffice.bronnum.dk', 'test-ucommerce.bronnum.dk'].includes(
        this.host
      )
        ? 'noindex, nofollow'
        : `${this.seo.noindex ? 'noindex' : 'index'} ${this.seo.nofollow ? 'nofollow' : 'follow'}`
      return {
        title: this.metaTitle(this.meta.type),
        htmlAttrs: {
          lang: this.meta?.culture ? this.meta?.culture.split('-')[0] : 'en'
        },
        bodyAttrs: {
          class: 'o-page' + (this.theme ? ` -${this.theme}` : '')
        },
        script: [
          {
            hid: 'orgSchema',
            type: 'application/ld+json',
            json: this.orgSchema && JSON.parse(this.orgSchema) ? JSON.parse(this.orgSchema) : ''
          },
          {
            hid: 'vwoCode',
            type: 'text/javascript',
            id: 'vwoCode',
            innerHTML: vwoCode
          }
        ],
        __dangerouslyDisableSanitizersByTagID: {
          vwoCode: ['innerHTML']
        },
        link: [
          {
            hid: 'canonical',
            rel: 'canonical',
            href: this.hostname + (this.seo?.canonicalUrl ? this.seo.canonicalUrl : this.$route.path)
          }
        ],
        meta: [
          // general
          {
            hid: 'description',
            name: 'description',
            content: this.firstDefined(this.social?.ogDescription, this.metaDesc(this.meta.type), this.product?.longDescription)
          },
          {
            hid: 'robots',
            name: 'robots',
            content: robots
          },
          // Facebook
          {
            hid: 'facebook-domain-verification',
            name: 'facebook-domain-verification',
            content: 'rodn9stpis6sbiyt4s2a3nc0aw5a0u'
          },
          {
            hid: 'og:title',
            property: 'og:title',
            content: this.firstDefined(this.social?.ogTitle, this.seo?.metaTitle, this.meta?.title)
          },
          {
            hid: 'og:description',
            property: 'og:description',
            content: this.firstDefined(this.social?.ogDescription, this.metaDesc(this.meta.type), this.product?.longDescription)
          },
          {
            hid: 'og:type',
            property: 'og:type',
            content: this.social?.ogType ? this.social?.ogType : 'website'
          },
          {
            hid: 'og:url',
            property: 'og:url',
            content: this.hostname + (this.seo?.canonicalUrl ? this.seo.canonicalUrl : this.$route.path)
          },
          {
            hid: 'og:image',
            property: 'og:image',
            content: this.firstDefined(
              this.social?.ogImage && this.social?.ogImage[0]?.url ? this.getImageUrl(this.social?.ogImage) : undefined,
              this.product?.images?.[0]?.url,
              this.logo
            )
          },
          // Twitter
          {
            hid: 'twitter:card',
            property: 'twitter:card',
            content: this.social?.twitterCard ? this.social?.twitterCard : 'summary'
          },
          {
            hid: 'twitter:title',
            property: 'twitter:title',
            content: this.firstDefined(this.social?.twitterTitle, this.seo?.metaTitle, this.meta?.title)
          },
          {
            hid: 'twitter:description',
            property: 'twitter:description',
            content: this.firstDefined(this.social?.twitterDescription, this.seo?.metaDescription, this.product?.longDescription)
          },
          {
            hid: 'twitter:image',
            property: 'twitter:image',
            content: this.firstDefined(
              this.social?.twitterImage && this.social?.twitterImage[0]?.url ? this.getImageUrl(this.social?.twitterImage) : undefined,
              this.product?.images?.[0]?.url,
              this.logo
            )
          },
          {
            hid: 'twitter:site',
            property: 'twitter:site',
            content: this.social?.twitterSite
          },
          {
            hid: 'twitter:creator',
            property: 'twitter:creator',
            content: this.social?.twitterCreator
          }
        ]
      }
    }
  }
}
</script>
